import {useMemo} from 'react'
import {Bell} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {ErrorLogLink} from '@/components/ErrorLogLink'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {Strong} from '@/components/shared/Strong'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {ErrorText} from '@/components/shared/ErrorText'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Pagination} from '@/components/ui/pagination'
import {SortableHeader} from '@/components/ui/sortable-header'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {NotificationControllerLogEndpoint} from '@/types/endpoint-contracts'
import {formatTimestamp} from '@/lib/date-utils'
import {routes} from '@/lib/routes'
import {NotificationStatus} from '@/types/notification'
import {PageLayout} from '@/components/shared/PageLayout'
import {useAdminNotificationLog} from './useNotifications'
import {getEventTypeLabel, getEventTypeBadgeVariant} from './notificationUtils'

export function AdminNotificationLog() {
    const i18n = useI18n()
    usePageTitle(i18n.notifications.adminLogPageTitle)
    const navigate = useNavigate()

    const {
        page, pageSize, sortBy, sortDir, queryString,
        setSort, setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: NotificationControllerLogEndpoint,
        defaultSortBy: NotificationControllerLogEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
        sortable: true,
    })

    const {data, isLoading, error} = useAdminNotificationLog(queryString)

    const totalItems = data?.totalElements || 0
    const totalPages = data?.totalPages || 0
    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    const getStatusBadge = (status: string) => {
        switch (status) {
            case NotificationStatus.SENT:
                return <Badge variant={BadgeVariant.SUCCESS} data-testid="notification-status-sent">{i18n.notifications.statusSent}</Badge>
            case NotificationStatus.PENDING:
                return <Badge variant="outline" data-testid="notification-status-pending">{i18n.notifications.statusPending}</Badge>
            case NotificationStatus.FAILED:
                return <Badge variant="destructive" data-testid="notification-status-failed">{i18n.notifications.statusFailed}</Badge>
            default:
                return <Badge variant="outline">{status}</Badge>
        }
    }

    if (error) {
        return <ErrorBanner error={error}/>
    }

    return (
        <PageLayout
            variant="list"
            icon={Bell}
            title={i18n.notifications.adminLogTitle}
            count={totalItems}
            description={`${i18n.notifications.notificationCount({count: totalItems})} ${hasActiveFilters ? i18n.notifications.matchingFilters : ''}`}
            data-testid="admin-notification-log"
            filters={
                <UnifiedSearchBar
                    contract={NotificationControllerLogEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.notifications.filterPlaceholder as string}
                    testIdNamespace="admin-notifications"
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-title">{i18n.notifications.title_column}</TableHead>
                        <TableHead className="text-center" data-testid="col-event-type">{i18n.notifications.eventType}</TableHead>
                        <TableHead data-testid="col-recipient">{i18n.notifications.recipient}</TableHead>
                        <TableHead className="text-center" data-testid="col-delivery-status">{i18n.notifications.deliveryStatus}</TableHead>
                        <TableHead data-testid="col-error">{i18n.notifications.error}</TableHead>
                        <TableHead data-testid="col-date">
                            <SortableHeader
                                label={i18n.notifications.date as string}
                                sortKey={NotificationControllerLogEndpoint.sort.CREATED_AT}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                        <TableHead data-testid="col-sent-at">{i18n.notifications.sentAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {isLoading ? (
                        <TableRow>
                            <TableCell colSpan={7} className="text-center py-8">
                                <MutedText>{i18n.common.loading}</MutedText>
                            </TableCell>
                        </TableRow>
                    ) : data?.content.length === 0 ? (
                        <TableEmptyState
                            colSpan={7}
                            hasFilters={!!hasActiveFilters}
                            matchMessage={i18n.notifications.noNotificationsMatch as string}
                            emptyMessage={i18n.notifications.noNotifications as string}
                        />
                    ) : (
                        data?.content.map((notification) => (
                            <TableRow
                                key={notification.id}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => navigate(routes.my.notifications.detail(notification.id))}
                                data-testid={`admin-notification-row-${notification.id}`}
                            >
                                <TableCell data-testid={`admin-notification-title-${notification.id}`}>
                                    <Strong>{notification.title}</Strong>
                                </TableCell>
                                <TableCell className="text-center">
                                    <Badge variant={getEventTypeBadgeVariant(notification.eventType)}>
                                        {getEventTypeLabel(notification.eventType, i18n.notifications)}
                                    </Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground" data-testid={`admin-notification-recipient-${notification.id}`}>
                                    {notification.recipientIdentityId}
                                </TableCell>
                                <TableCell className="text-center">
                                    {getStatusBadge(notification.notificationStatus)}
                                </TableCell>
                                <TableCell>
                                    {notification.errorMessage ? (
                                        <InlineRow gap="xs">
                                            <ErrorText size="xs" truncate title={notification.errorMessage} className="max-w-[200px]" data-testid={`admin-notification-error-${notification.id}`}>
                                                {notification.errorMessage}
                                            </ErrorText>
                                            <ErrorLogLink errorLogRef={notification.errorLogRef} sourceType="Notification" sourceId={notification.id}/>
                                        </InlineRow>
                                    ) : (
                                        <MutedText>{'\u2014'}</MutedText>
                                    )}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {formatTimestamp(notification.createdAt)}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {notification.sentAt ? formatTimestamp(notification.sentAt) : '\u2014'}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
